import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { testCloudflareConnection } from '@/lib/customDomain';

type TestState = 'idle' | 'testing' | 'success' | 'error';

/**
 * Carte « Cloudflare » du Super Admin (Paramètres → Intégrations).
 * Affiche le statut de configuration SANS jamais exposer le token (le token
 * vit uniquement dans les secrets backend, lu par l'Edge Function).
 */
export default function CloudflareSettings() {
  const { user } = useAuth();
  const [testState, setTestState] = useState<TestState>('idle');
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  const runTest = useCallback(async () => {
    if (!user) return;
    setTestState('testing');
    setTestError(null);
    try {
      const res = await testCloudflareConnection({ userId: user.id, userEmail: user.email });
      setConfigured(!!res.configured);
      if (res.ok) {
        setTestState('success');
      } else {
        setTestState('error');
        setTestError(res.error || 'Erreur de connexion Cloudflare.');
      }
    } catch (err) {
      setTestState('error');
      setConfigured(false);
      setTestError(err instanceof Error ? err.message : 'Erreur de connexion Cloudflare.');
    }
  }, [user]);

  // Vérifie l'état de configuration au montage (sans exposer de secret)
  useEffect(() => {
    runTest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const configuredLabel =
    configured === null ? '…' : configured ? 'Configuré' : 'Non configuré';

  return (
    <section className="bg-background-50 border border-background-200/70 rounded-lg p-5">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
          <i className="ri-cloud-line text-lg"></i>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="text-sm font-semibold text-foreground-900">Cloudflare API</h3>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
              configured ? 'bg-green-100 text-green-700' : 'bg-background-100 text-foreground-500'
            }`}>
              {configuredLabel}
            </span>
          </div>
          <p className="text-xs text-foreground-500 mb-3">
            Cloudflare for SaaS / Custom Hostnames — utilisé pour la gestion automatique des
            domaines personnalisés des commerçants (création, validation SSL, suppression).
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div className="p-3 rounded-lg bg-background-100">
              <div className="text-[10px] text-foreground-400 uppercase tracking-wide mb-1">Zone ID configuré</div>
              <div className="text-sm font-medium text-foreground-900">
                {configured === null ? '…' : configured ? 'Oui' : 'Non'}
              </div>
            </div>
            <div className="p-3 rounded-lg bg-background-100">
              <div className="text-[10px] text-foreground-400 uppercase tracking-wide mb-1">API Token configuré</div>
              <div className="text-sm font-medium text-foreground-900 font-mono">
                {configured ? '••••••••••••••' : configured === null ? '…' : 'Non'}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={runTest}
              disabled={testState === 'testing'}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap bg-foreground-950 text-background-50 hover:bg-foreground-800 transition-colors cursor-pointer disabled:opacity-50"
            >
              {testState === 'testing' ? (
                <><i className="ri-loader-4-line animate-spin"></i>Test en cours…</>
              ) : (
                <><i className="ri-plug-line"></i>Tester la connexion Cloudflare</>
              )}
            </button>

            {testState === 'success' && (
              <span className="text-xs font-medium text-green-600 flex items-center gap-1">
                <i className="ri-checkbox-circle-line"></i>Connexion Cloudflare réussie
              </span>
            )}
            {testState === 'error' && (
              <span className="text-xs font-medium text-red-600 flex items-center gap-1">
                <i className="ri-error-warning-line"></i>Erreur de connexion Cloudflare
              </span>
            )}
          </div>

          {testState === 'error' && testError && (
            <p className="text-[11px] text-red-500 mt-2">{testError}</p>
          )}

          <p className="text-[10px] text-foreground-400 mt-3 flex items-center gap-1.5">
            <i className="ri-shield-check-line text-accent-500"></i>
            Le token Cloudflare est stocké dans les secrets sécurisés du backend et n&apos;est jamais
            exposé au navigateur.
          </p>
        </div>
      </div>
    </section>
  );
}